import React from "react";
import { API } from "common";
import first from 'lodash/first';
import type { CouponData } from 'components/new-appointment/Coupon';
import type { CategoryRaw, ServiceRaw, Staff } from 'types/models';

// Shape of a customer row from customer/getAllCustomerAutoSearchPage; only
// `id` and `name` are read by callers of getCustomerInfoByPhone.
export interface CustomerSearchResult {
  id: string | number;
  name: string;
  phone?: string;
}

export interface BookingAppointmentPayload {
  // review.tsx converts to number; booking/index.tsx forwards the raw
  // customer id (string or number) as-is — both are genuine call shapes.
  customerId: number | string | null;
  customerName: string;
  phone: string;
  bookingType: number;
  serviceIds: number[];
  staffId: number | null;
  totalPeople: number;
  startTime: string;
  endTime: string;
  note: string;
  isAnybody: number;
  apiKey?: string;
  requestStaff: boolean;
  couponCode: string;
  id: null;
}

// Success payload shape isn't fully known; failures are returned (not thrown)
// as the raw error response body, which may carry a `message`.
export interface BookingResult {
  message?: string;
  [key: string]: unknown;
}

const fetchCategories = async (): Promise<CategoryRaw[] | null> => {
  try {
    const response = await API.get(`service/category/list`);
    if (response?.data?.payload) {
      return response.data.payload;
    }
    throw 'fail';
  } catch (error) {
    return null;
  }
};
const fetchServices = async (): Promise<ServiceRaw[] | null> => {
  try {
    const response = await API.get(`service/list`);
    if (response?.data?.payload) {
      return response.data.payload;
    }
    throw 'fail';
  } catch (error) {
    return null;
  }
};

const fetchStaffs = async (): Promise<Staff[] | null> => {
  try {
    const response = await API.get(`staff/list`);
    if (response?.data?.payload) {
      return response.data.payload;
    }
    throw 'fail';
  } catch (error) {
    return null;
  }
};

export const getCustomerInfoByPhone = async (phone: string): Promise<CustomerSearchResult | null> => {
  try {
    const response = await API.get('customer/getAllCustomerAutoSearchPage?keyWord=' + phone);
    if (response?.data?.payload?.customers?.length > 0) {
      return first(response.data.payload?.customers);
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};

export const getCouponByQRcode = async (code: string): Promise<CouponData | null> => {
  try {
    const response = await API.get('coupon/getCouponByQrcode?qrCode=' + code);
    if (response.data.payload) {
      return response.data.payload;
    }
    return null;
  } catch (error) {
    return null;
  }
}

export const getTimeSlots = async (
  staffId: string | number | null | undefined,
  currentDate: string
): Promise<string[] | null> => {
  const params: { currentDate: string; staffId?: string | number | null } = { currentDate, staffId };
  if (!params.staffId) {
    delete params.staffId;
  }
  try {
    const response = await API.get('appointment/getAvailableTimeSlotByStaff', { params })
    if (response.data?.payload?.timeSlots) {
      return response.data?.payload?.timeSlots;
    }
    return null;
  } catch (error) {
    return null;
  }
}

export const makeAppointment = async (payload: BookingAppointmentPayload): Promise<BookingResult | null> => {
  const body = {
    type: 'FEEDBACK/DASHBOARD/ON_CREATE_APPOITMENT',
    payload
  }

  try {
    const response = await API.post('appointment/v2/bookingAppointment', body);
    if (response.data.payload) {
      return response.data.payload;
    }
    return null;
  } catch (error: any) {
    return error?.response?.data;
  }
}

const useFetchBookingData = () => {
  return async () => {
    return await Promise.all([fetchStaffs(), fetchCategories(), fetchServices()]);
  };
};

export default useFetchBookingData;
